/**
 * Top Firestore collections by aggregate read count across all audits
 * in the 24h window. Pulls from `infra.reads_by_collection` in the
 * `audit_cost_summary` row.
 */
import { ViewQueryButton } from "./ViewQueryButton";
import { formatInt } from "./format";
import type { ApiEnvelope, HotCollectionsData } from "./types";

interface HotCollectionsProps {
  envelope: ApiEnvelope<HotCollectionsData> | undefined;
  loading: boolean;
}

export function HotCollections({ envelope, loading }: HotCollectionsProps) {
  const rows = envelope?.data.rows ?? [];
  const max = Math.max(...rows.map((r) => r.total_reads), 1);

  return (
    <section className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
            Hot Firestore collections (24h)
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Top collections by aggregate read count across all audits.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="py-6 text-center text-sm text-zinc-500">loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-6 text-center text-sm text-zinc-500">
          No collection read activity yet today.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left py-1">Collection</th>
              <th className="text-right py-1">Reads</th>
              <th className="text-right py-1">Audits</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 10).map((r) => {
              const pct = (r.total_reads / max) * 100;
              return (
                <tr key={r.collection} className="border-t border-zinc-100 dark:border-zinc-800">
                  <td className="py-1.5">
                    <div className="font-mono text-xs">{r.collection}</div>
                    <div className="mt-1 h-1 rounded bg-zinc-100 dark:bg-zinc-800">
                      <div
                        className="h-full rounded bg-blue-500"
                        style={{ width: `${pct.toFixed(2)}%` }}
                      />
                    </div>
                  </td>
                  <td className="text-right text-xs font-semibold py-1.5 align-top">
                    {formatInt(r.total_reads)}
                  </td>
                  <td className="text-right text-xs text-zinc-500 py-1.5 align-top">
                    {formatInt(r.audit_count)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="mt-3">
        <ViewQueryButton query={envelope?.query} />
      </div>
    </section>
  );
}
